<template>
  <ExpansionPanel
    :key="sourceType"
    class="w-full"
    :model-value="expansionPanel.isShown.value"
  >
    <template #activator="{ isShown }">
      <button
        class="flex justify-between items-center w-full py-[10px] pl-4 pr-2"
        :class="[
          buttonClass,
          {
            'bg-blue-50': isShown,
          },
        ]"
        @click="togglePanel"
      >
        <div class="flex gap-2 items-center">
          <div
            class="bg-slate-300 rounded-full w-2 h-2"
            :class="{
              '!bg-blue-800': isShown,
            }"
          />
          <div class="text-sm font-medium text-slate-700">{{ sourceType }}</div>
        </div>
        <ChevronDownBoldIcon v-if="isShown" class="w-5 h-5 text-primary" />
        <ChevronRightBoldIcon v-else class="w-5 h-5 text-primary" />
      </button>
    </template>
    <template #default>
      <ReviewItemTypeCard
        v-if="sourceType === 'Peer-reviewed literature'"
        :sources="PeerReviewedLiteratureSources"
        :is-plan-unlocked="isPlanEditable"
        @add-import-source="handleAddImportSource"
      />

      <ReviewItemTypeCard
        v-if="sourceType === 'Gray literature'"
        :sources="grayLiteratureSources"
        :is-plan-unlocked="isPlanEditable"
        @add-import-source="handleAddImportSource"
      />

      <ReviewItemTypeCard
        v-if="sourceType === 'Systematic reviews'"
        :sources="scholarlyArticlesSystematicReviewSources"
        :is-plan-unlocked="isPlanEditable"
        @add-import-source="handleAddImportSource"
      />

      <ReviewItemTypeCard
        v-if="sourceType === 'Clinical trials'"
        :sources="clinicalTrialsSources"
        :is-plan-unlocked="isPlanEditable"
        @add-import-source="handleAddImportSource"
      >
        <div class="pl-6 pt-0">
          <ul class="ml-1 pl-2 space-y-2 tree">
            <li
              v-for="(provider, index) in clinicalTrialDataProviders"
              :key="index"
              class="sub-item"
            >
              <div class="flex items-center gap-3 flex-1">
                <component :is="provider.icon" class="rounded-full w-5 h-5" />
                <span
                  class="text-[13px] line-clamp-1 w-4/5 font-medium text-slate-700"
                >
                  {{ provider.name }}
                </span>
              </div>
            </li>
          </ul>
        </div>
      </ReviewItemTypeCard>
      <IncidentsSearch
        v-if="sourceType === 'Safety: Incidents'"
        :is-plan-editable="isPlanEditable"
      >
      </IncidentsSearch>

      <SafetyInformationSearch
        v-if="sourceType === 'Safety: FSNs, FSCAs, bans, recalls, etc...'"
        v-slot="{ show }"
      >
        <div class="relative group w-full">
          <div class="flex-1 text-left w-full">
            <PlanLockGuard>
              <button
                class="w-full hover:bg-blue-50 pl-7 py-2"
                :disabled="!isPlanEditable"
                @click="show"
              >
                <ul class="space-y-2">
                  <li
                    v-for="(provider, index) in fieldSafetyNoticesProviders"
                    :key="index"
                    class="w-full flex gap-3"
                  >
                    <img
                      :src="provider.icon"
                      class="rounded-full w-5 h-5 object-cover object-center"
                      alt="Country Flag"
                    />
                    <span
                      class="text-[13px] line-clamp-1 font-medium text-slate-700 text-left"
                    >
                      {{ provider.name }}
                    </span>
                  </li>
                </ul>
              </button>
            </PlanLockGuard>
          </div>
        </div>
      </SafetyInformationSearch>

      <HtaSearch
        v-if="sourceType === 'Health technology assessments'"
        v-slot="{ show }"
      >
        <PlanLockGuard>
          <button
            class="flex justify-between items-center w-full py-[10px] pl-7 pr-2.5 gap-2 hover:bg-blue-50"
            :disabled="!isPlanEditable"
            @click="show"
          >
            <div
              class="flex text-left items-center gap-2 text-slate-700 text-sm font-medium"
            >
              <img
                src="/sources-icons/inahta.png"
                class="w-6 h-6"
                alt="source icon"
              />
              <span class="line-clamp-1">INAHTA</span>
              <div class="pl-6 pt-0">
                <ul class="ml-1 pl-2 space-y-2 tree">
                  <li
                    v-for="(provider, index) in htaDataProviders"
                    :key="index"
                    class="sub-item"
                  >
                    <div class="flex items-center gap-3 flex-1">
                      <img
                        :src="provider.icon"
                        class="rounded-full w-5 h-5 object-cover object-center"
                        alt="Country Flag"
                      />
                      <span
                        class="text-[13px] line-clamp-1 w-4/5 font-medium text-slate-700"
                      >
                        {{ provider.name }}
                      </span>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </button>
        </PlanLockGuard>
      </HtaSearch>

      <!-- <ReviewItemTypeCard
        v-if="sourceType === 'Standards'"
        :sources="standardsSources"
        :is-plan-unlocked="isPlanEditable"
        @add-import-source="handleAddImportSource"
      /> -->
      <!-- <GuidanceSearch
        v-if="sourceType === 'Guidance'"
        :guidance-sources="guidanceSources"
        :is-plan-editable="isPlanEditable"
      /> -->
    </template>
  </ExpansionPanel>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import ReviewItemTypeCard from '../AddImportSourceToPlan/ReviewItemTypeCard.vue'
import ExpansionPanel from '@app/components/Global/ExpansionPanel.vue'
import useBuiltInImportSources from '@app/composables/use-built-in-import-sources'
import { injectStrict } from '@app/utils/injectStrict'
import { ReviewKey } from '../../../use-review'
import { ImportSourceType } from '@core/domain/types/import-source-type.type'
import { isUrlValid } from '@app/utils/urlValidation'
import useLoading from '@app/composables/use-loading'
import useSnackbar from '@app/composables/use-snackbar'
import { SnackbarState } from '@app/types'
import { HttpException } from '@core/exceptions/http.exception'
import { errorMessage } from '@app/utils/error-message'
import { BuiltInImportSourceId } from '@core/domain/types/builtInImportSourceId'
import {
  AustraliaFlag,
  ChinaFlag,
  USAFlag,
  EUFlag,
  NetherlandsFlag,
  BrazilFlag,
  IndiaFlag,
  SouthKoreaFlag,
  CubaFlag,
  GermanyFlag,
  IranFlag,
  JapanFlag,
  SouthAfricaFlag,
  SriLankaFlag,
  ThailandFlag,
  PeruFlag,
  LebanonFlag,
  UKFlag,
} from '@app/components/Icons/flags/index'
import SafetyInformationSearch from './SafetyInformationSearch/SafetyInformationSearch.vue'
import useExpansionPanel from '@app/composables/use-expansion-panel'
import ChevronDownBoldIcon from '@app/components/Icons/ChevronDownBoldIcon.vue'
import ChevronRightBoldIcon from '@app/components/Icons/ChevronRightBoldIcon.vue'
import { SafetyInformationSourcesKey } from '@app/injectionKeys'
import IncidentsSearch from './IncidentsSearch/IncidentsSearch.vue'
import HtaSearch from './HtaSearch/HtaSearch.vue'
//import GuidanceSearch from './GuidanceSearch/GuidanceSearch.vue'
import PlanLockGuard from '../../PlanLockGuard.vue'

const safetyInformationSources = injectStrict(SafetyInformationSourcesKey)
const expansionPanel = useExpansionPanel()

defineProps<{
  sourceType: string
}>()

interface DataProvider {
  name: string
  icon: any
}

const clinicalTrialDataProviders: DataProvider[] = [
  {
    name: 'ANZCTR',
    icon: AustraliaFlag,
  },
  { name: 'ChiCTR', icon: ChinaFlag },
  { name: 'ClinicalTrials.gov', icon: USAFlag },
  { name: 'CTIS', icon: EUFlag },
  { name: 'EU-CTR', icon: EUFlag },
  { name: 'ISRCTN', icon: UKFlag },
  { name: 'OMON', icon: NetherlandsFlag },
  { name: 'ReBec', icon: BrazilFlag },
  { name: 'CTRI', icon: IndiaFlag },
  {
    name: 'CRiS',
    icon: SouthKoreaFlag,
  },
  { name: 'RPCEC', icon: CubaFlag },
  { name: 'DRKS', icon: GermanyFlag },
  { name: 'IRCT', icon: IranFlag },
  { name: 'jRCT', icon: JapanFlag },
  { name: 'PACTR', icon: SouthAfricaFlag },
  { name: 'SLCTR', icon: SriLankaFlag },
  { name: 'TCTR', icon: ThailandFlag },
  { name: 'REPEC', icon: PeruFlag },
  { name: 'LBCTR', icon: LebanonFlag },
  {
    name: 'ITMCTR',
    icon: ChinaFlag,
  },
]

const htaDataProviders: DataProvider[] = [
  { name: 'ACE', icon: 'https://flagcdn.com/sg.svg' },
  { name: 'AETS', icon: 'https://flagcdn.com/es.svg' },
  { name: 'AETSA', icon: 'https://flagcdn.com/es.svg' },
  { name: 'Agenas', icon: 'https://flagcdn.com/it.svg' },
  { name: 'AHRQ', icon: 'https://flagcdn.com/us.svg' },
  { name: 'AHTA', icon: 'https://flagcdn.com/au.svg' },
  { name: 'AIHTA', icon: 'https://flagcdn.com/at.svg' },
  { name: 'ANS', icon: 'https://flagcdn.com/br.svg' },
  { name: 'AOTMiT', icon: 'https://flagcdn.com/pl.svg' },
  { name: 'AP-HP', icon: 'https://flagcdn.com/fr.svg' },
  { name: 'AQuAS', icon: 'https://flagcdn.com/es.svg' },
  { name: 'ASERNIP-S', icon: 'https://flagcdn.com/au.svg' },
  { name: 'AVALIA-T', icon: 'https://flagcdn.com/es.svg' },
  { name: 'C2H', icon: 'https://flagcdn.com/jp.svg' },
  { name: 'CDA-AMC', icon: 'https://flagcdn.com/ca.svg' },
  { name: 'CDE', icon: 'https://flagcdn.com/tw.svg' },
  { name: 'CONITEC', icon: 'https://flagcdn.com/br.svg' },
  { name: 'DHQI', icon: 'https://flagcdn.com/dk.svg' },
  { name: 'DIGEMID', icon: 'https://flagcdn.com/pe.svg' },
  { name: 'FinCCHTA', icon: 'https://flagcdn.com/fi.svg' },
  { name: 'G-BA', icon: 'https://flagcdn.com/de.svg' },
  { name: 'GÖG', icon: 'https://flagcdn.com/at.svg' },
  { name: 'HAD-Uruguay', icon: 'https://flagcdn.com/uy.svg' },
  { name: 'HAS', icon: 'https://flagcdn.com/fr.svg' },
  { name: 'HIQA', icon: 'https://flagcdn.com/ie.svg' },
  { name: 'HIS', icon: 'https://flagcdn.com/gb-sct.svg' },
  { name: 'HTAIn', icon: 'https://flagcdn.com/in.svg' },
  { name: 'HTW', icon: 'https://flagcdn.com/gb-wls.svg' },
  { name: 'IACS', icon: 'https://flagcdn.com/es.svg' },
  { name: 'IECS', icon: 'https://flagcdn.com/ar.svg' },
  { name: 'IETS', icon: 'https://flagcdn.com/co.svg' },
  { name: 'IHE', icon: 'https://flagcdn.com/ca.svg' },
  { name: 'INEAS', icon: 'https://flagcdn.com/tn.svg' },
  { name: 'INESSS', icon: 'https://flagcdn.com/ca.svg' },
  { name: 'IQWiG', icon: 'https://flagcdn.com/de.svg' },
  { name: 'KCE', icon: 'https://flagcdn.com/be.svg' },
  { name: 'MaHTAS', icon: 'https://flagcdn.com/my.svg' },
  { name: 'NECA', icon: 'https://flagcdn.com/kr.svg' },
  { name: 'NICE', icon: 'https://flagcdn.com/gb.svg' },
  { name: 'NIHO', icon: 'https://flagcdn.com/sk.svg' },
  { name: 'NIHR', icon: 'https://flagcdn.com/gb.svg' },
  { name: 'NIPH', icon: 'https://flagcdn.com/no.svg' },
  { name: 'OH', icon: 'https://flagcdn.com/ca.svg' },
  { name: 'OSTEBA', icon: 'https://flagcdn.com/es.svg' },
  { name: 'PHARMAC', icon: 'https://flagcdn.com/nz.svg' },
  { name: 'RER', icon: 'https://flagcdn.com/it.svg' },
  { name: 'SK-NRCHD', icon: 'https://flagcdn.com/kz.svg' },
  { name: 'SBU', icon: 'https://flagcdn.com/se.svg' },
  { name: 'SFOPH', icon: 'https://flagcdn.com/ch.svg' },
  { name: 'UVT', icon: 'https://flagcdn.com/it.svg' },
  { name: 'ZIN', icon: 'https://flagcdn.com/nl.svg' },
  { name: 'ZonMw', icon: 'https://flagcdn.com/nl.svg' },
]

const fieldSafetyNoticesProviders = safetyInformationSources.map((source) => ({
  name: source.name,
  icon: source.country.flagUrl,
}))

const builtInImportSources = useBuiltInImportSources()
const review = injectStrict(ReviewKey)
const loading = useLoading()
const snackbar = useSnackbar()

const isPlanEditable = computed(() => !review.isPlanReadonly.value)

const PeerReviewedLiteratureSources = computed(() => {
  const sources = [
    createSource({
      id: BuiltInImportSourceId.PUBMED,
      iconUrl: '/sources-icons/pubmed.png',
    }),
    createSource({
      id: BuiltInImportSourceId.OPEN_ALEX,
      iconUrl: '/sources-icons/openalex.png',
    }),
    createSource({
      id: BuiltInImportSourceId.DIMENSIONS_AI,
      iconUrl: '/sources-icons/dimensions-ai.png',
    }),
    createSource({
      id: BuiltInImportSourceId.PMC,
      iconUrl: '/sources-icons/pmc.png',
    }),
    createSource({
      id: BuiltInImportSourceId.EUROPE_PMC,
      iconUrl: '/sources-icons/europe-pmc.png',
    }),
    createSource({
      id: BuiltInImportSourceId.EMBASE,
      iconUrl: '/sources-icons/science-direct.png',
    }),
    createSource({
      id: BuiltInImportSourceId.LIVIVO,
      iconUrl: '/sources-icons/livivo.png',
      isComingSoon: true,
    }),
  ]
  return sources
})

const grayLiteratureSources = computed(() => {
  const sources = [
    createSource({
      id: BuiltInImportSourceId.GOOGLE_SCHOLAR,
      iconUrl: '/sources-icons/google-scholar.png',
    }),
  ]
  return sources
})

const scholarlyArticlesSystematicReviewSources = computed(() => {
  const sources = [
    createSource({
      id: BuiltInImportSourceId.COCHRANE,
      iconUrl: '/sources-icons/cochrane.png',
    }),
    createSource({
      id: BuiltInImportSourceId.PROSPERO,
      iconUrl: '/sources-icons/prospero.png',
    }),
  ]
  return sources
})

const clinicalTrialsSources = computed(() => {
  const sources = [
    createSource({
      id: BuiltInImportSourceId.ICTRP,
      iconUrl: '/sources-icons/ictrp.svg',
    }),
  ]
  return sources
})

/* const standardsSources = computed(() => {
  const sources = [
    createSource({
      id: BuiltInImportSourceId.CENELEC,
      iconUrl: '/sources-icons/cenelec.png',
      icon: '',
      isComingSoon: true,
    }),
  ]
  return sources
}) */

/* const guidanceSources = computed(() => {
  const sources = [
    createSource({
      id: BuiltInImportSourceId.MDCG,
      iconUrl: '/sources-icons/mdcg.png',
    }),
  ]
  return sources
}) */

function createSource({
  id,
  iconUrl,
  icon,
  isComingSoon,
}: {
  id: BuiltInImportSourceId
  iconUrl: string
  icon?: any
  isComingSoon?: boolean
}) {
  const source = builtInImportSources.findById(id)!
  return {
    label: source.name,
    id: source.id,
    iconUrl,
    isEvidenceMetadataExtractionTechnology:
      source.isEvidenceMetadataExtractionTechnology,
    type: source.type,
    url: source.url,
    description: source.description,
    icon,
    isComingSoon,
  }
}

async function handleAddImportSource(data: {
  id: string
  name: string
  url?: string
  description?: string
  type: ImportSourceType
}) {
  if (
    data.type !== ImportSourceType.HAND_SEARCH &&
    data.type !== ImportSourceType.CITATION_SEARCH &&
    (!data.url || !isUrlValid(data.url))
  ) {
    throw new Error('Invalid URL')
  }
  loading.start()
  try {
    if (!isSourceUsedInReview(data.id)) {
      await review.addImportSourceToPlan({
        id: data.id,
        name: data.name,
        url: data.url ?? '',
        description: data.description,
        type: data.type,
      })
      snackbar.success('Success')
      await new Promise((resolve) => setTimeout(resolve, 1000))
    }
    document.getElementById(data.id)?.scrollIntoView({ behavior: 'smooth' })
  } catch (e) {
    const error = e as HttpException
    snackbar.show(SnackbarState.ERROR, errorMessage(error.response.data))
    if (error.response.data.statusCode >= 500) {
      throw e
    }
  } finally {
    loading.stop()
  }
}

function isSourceUsedInReview(sourceId: string) {
  return review.entity.value.plan?.importPlan?.importSources?.some(
    (s) => s.id === sourceId,
  )
}

const buttonClass = computed(() => ({
  'hover:bg-blue-50': true,
}))

function togglePanel() {
  expansionPanel.toggle()
}
</script>

<style scoped>
.tree {
  position: relative;
  padding-left: 20px;
}

.item {
  position: relative;
}

.sub-item {
  position: relative;
}

.sub-item::before {
  content: '';
  position: absolute;
  left: -10px;
  top: 0;
  bottom: 0;
  width: 1px;
  background-color: #cbd5e1;
  height: 150%;
}

.sub-item span::before {
  content: '';
  position: absolute;
  left: -10px;
  top: 50%;
  width: 8px;
  height: 1px;
  background-color: #cbd5e1;
}

.sub-item:last-child::before {
  height: 50%;
}
</style>
